import {
  Color,
  ConsultantReferenceDocumentList,
  DocumentList,
  DraftIcon,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  makeStyles,
  Typography
} from "@material-ui/core";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import React, { useEffect, useMemo } from "react";
import { DevTool } from "@hookform/devtools";
import {
  ConsultantPresentationCountry,
  ConsultantReferenceType,
  useGetConsultantShareableFilesLazyQuery,
  useSendToMedloMutation
} from "@graphql";
import { SpinnerOverlay } from "@common";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

type Props = {
  consultantId: string;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const ReferenceSendToMedloModal = ({ consultantId, open, setOpen }: Props) => {
  const classes = useStyles();
  const snack = useSnackbarsPipeline();
  const form = useForm<any>({
    defaultValues: {
      referenceIds: [],
      cvDocuments: [],
      crmCv: false
    },
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const [sendToMedlo, { loading }] = useSendToMedloMutation();

  const handleSubmit = async () => {
    const infoSnack = snack.showInfo("Skickar konsult till Medlo...", null, {
      persist: true
    });

    const refIds = form.getValues().referenceIds.map((x) => x.referenceId);

    sendToMedlo({
      variables: {
        consultantId,
        referenceIds: refIds ?? [],
        crmCv: form.getValues().crmCv,
        cvDocuments: form.getValues().cvDocuments
      }
    })
      .then(({ data }) => {
        if (data.sendConsultantToMedlo.success) {
          snack.showSuccess("Konsult skickad till Medlo");
        }

        if (data.sendConsultantToMedlo.message) {
          const msg = (
            <div>
              {data.sendConsultantToMedlo.message.split("\n").map((x) => (
                <Typography>{x}</Typography>
              ))}
            </div>
          );

          if (data.sendConsultantToMedlo.success) {
            snack.showInfo(msg as any, "Varningar", {
              persist: true
            });
          } else {
            snack.showError(msg as any, "Fel vid skicka till Medlo", {
              persist: true
            });
          }
        }
      })
      .catch((e) => {
        snack.showError(e.message);
      })
      .finally(() => {
        snack.closeSnackbar(infoSnack);
        setOpen(false);
      });
  };

  const [getFilesQuery, { data, loading: documentLoading }] =
    useGetConsultantShareableFilesLazyQuery({
      variables: { consultantId: consultantId },
      fetchPolicy: "network-only",
      onError: (err) => {
        console.error(err);
        snack.showError(locale.ErrorOccurred);
      }
    });

  const { crmCv, cvDocuments } = form.watch();

  useEffect(() => {
    if (open) {
      getFilesQuery();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      form.trigger();
    } else {
      form.reset();
    }
  }, [crmCv, cvDocuments]);

  const canSubmit = form.formState.isValid && !loading;

  const referenceDocuments = useMemo(() => {
    return (
      data?.consultantReferenceDocuments.filter((x) => {
        return [
          ConsultantReferenceType.Reference,
          ConsultantReferenceType.Evaluation
        ].includes(x.type);
      }) ?? []
    );
  }, [data?.consultantReferenceDocuments]);

  const nationalReferenceDocuments = useMemo(() => {
    return (
      data?.consultantReferenceDocuments.filter(
        (x) => x.type === ConsultantReferenceType.UUCrossQuestions
      ) ?? []
    );
  }, [data?.consultantReferenceDocuments]);

  const CVdocuments = useMemo(() => {
    return (
      data?.consultantById.DocumentsShareable.filter((x) => x.DocumentType === "CV") ?? []
    );
  }, [data?.consultantById.DocumentsShareable]);

  if (documentLoading) {
    return <SpinnerOverlay show />;
  }

  return (
    <FormProvider {...form}>
      <Dialog open={open} maxWidth="md" fullWidth onClose={() => setOpen(false)}>
        <DevTool control={form.control} placement={"top-right"} />
        <DialogTitle>
          <Typography variant={"h4"}>
            {"Välj vilka CV-filer samt referensfiler du vill skicka med till MEDLO:"}
          </Typography>
          <Typography variant={"h4"}>
            {
              "Observera att vi kommer skriva över det som finns i Medlo, så välj allting du vill ha med i presentationen."
            }
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {loading && <SpinnerOverlay show />}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {referenceDocuments?.length > 0 && (
                <Controller
                  render={({ field }) => (
                    <ConsultantReferenceDocumentList
                      {...field}
                      documents={referenceDocuments}
                      groupingKey={locale.References}
                    />
                  )}
                  name="referenceIds"
                  control={form.control}
                />
              )}
              {nationalReferenceDocuments?.length > 0 && (
                <Controller
                  render={({ field }) => (
                    <ConsultantReferenceDocumentList
                      {...field}
                      documents={nationalReferenceDocuments}
                      groupingKey={locale.ReferencesNational}
                    />
                  )}
                  name="referenceIds"
                  control={form.control}
                />
              )}
              {CVdocuments?.length > 0 && (
                <Box>
                  <Controller
                    render={({ field }) => (
                      <DocumentList
                        {...field}
                        files={CVdocuments ?? []}
                        error={form.getFieldState("cvDocuments").error?.message}
                      />
                    )}
                    name="cvDocuments"
                    control={form.control}
                  />
                </Box>
              )}

              <Controller
                control={form.control}
                name="crmCv"
                render={({ field }) => (
                  <FormControlLabel
                    className={form.getFieldState("crmCv").error ? classes.error : ""}
                    style={{ paddingLeft: 8 }}
                    control={
                      <Checkbox
                        checked={field.value}
                        color="primary"
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={
                      <div className={classes.docIcon}>
                        <DraftIcon htmlColor={Color.DetailsInput} />
                        "CRM-generated CV"
                        <OpenInNewIcon
                          fontSize={"small"}
                          htmlColor={Color.DetailsInput}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(
                              `/consultant-cv/${consultantId}/pdf?country=${ConsultantPresentationCountry.Sweden}`
                            );
                          }}
                        />
                      </div>
                    }
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box className={classes.btnWrap}>
            <Button
              color={"primary"}
              className={classes.cancelBtn}
              onClick={() => setOpen(false)}
            >
              {locale.Cancel}
            </Button>

            <Button
              color="primary"
              variant={"contained"}
              disabled={!canSubmit}
              onClick={() => {
                handleSubmit();
              }}
            >
              Ok
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
};

const schema = yup.object().shape(
  {
    referenceIds: yup.array().of(yup.string().required()),
    cvDocuments: yup.array(),
    crmCv: yup.boolean().nullable(true)
  },
  [["cvDocuments", "crmCv"]]
);

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    overflow: "hidden"
  },
  btnWrap: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
    padding: theme.spacing(2),
    gap: theme.spacing(2)
  },
  cancelBtn: {
    marginRight: "auto"
  },
  docIcon: {
    display: "flex",
    alignItems: "flex-end",
    gap: 5
  },
  error: {
    color: theme.palette.error.main
  }
}));
